import { useEffect, useState } from 'react'

/**
 * Force expand state type for synchronizing expand/collapse across components
 */
export interface ForceExpandState {
  /** Increment to trigger state change */
  generation: number
  /** Whether to expand (true) or collapse (false) */
  expanded: boolean
}

/**
 * Hook to synchronize expand/collapse state with a parent's forceExpandState control.
 *
 * When the parent updates forceExpandState (by incrementing generation), this hook
 * will automatically update the local isExpanded state to match.
 *
 * @param forceExpandState - Parent's force expand state (null if not controlled)
 * @param defaultExpanded - Initial expanded state (default: false)
 * @returns Tuple of [isExpanded, setIsExpanded]
 *
 * @example
 * function ChildComponent({ forceExpandState }) {
 *   const [isExpanded, setIsExpanded] = useForceExpandState(forceExpandState, true)
 *
 *   return (
 *     <button onClick={() => setIsExpanded(!isExpanded)}>
 *       {isExpanded ? 'Collapse' : 'Expand'}
 *     </button>
 *   )
 * }
 */
export function useForceExpandState(
  forceExpandState: ForceExpandState | null | undefined,
  defaultExpanded = false,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  // Respond to global expand/collapse toggle
  // biome-ignore lint/correctness/useExhaustiveDependencies: generation is the trigger for updates
  useEffect(() => {
    if (forceExpandState) {
      setIsExpanded(forceExpandState.expanded)
    }
  }, [forceExpandState?.generation])

  return [isExpanded, setIsExpanded]
}
